import axios from 'axios'
import submit from '../router/submit'
import router from '../router'
import queue from '../router/queue'
import helpers from '../helpers'
import _ from 'lodash'
import { DateTime } from 'luxon'
import { i18n } from '../i18n'

export const actions = {
  login ({ commit, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      axios.get('/sanctum/csrf-cookie')
        .then(() => {
          submit.post('/login', payload)
            .then(() => {
              commit('removeFromQueue')
              dispatch('showUser')
              resolve()
            }).catch(error => reject(error))
        })
    })
  },
  logout ({ commit }) {
    return new Promise((resolve) => {
      axios.post('/logout').then(response => {
        commit('auth', null)
        router.push({ name: 'Login' })
        resolve(response)
      })
    })
  },
  assumeUser ({ commit, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      axios.get('/assume/' + payload.id)
        .then(() => {
          dispatch('showUser')
            .then(() => {
              commit('submitting', false)
              router.push({ name: 'Dashboard' })
            })
          resolve()
        }).catch(error => reject(error))
    })
  },
  showUser ({ state, commit }) {
    if (state.online) {
      return new Promise((resolve, reject) => {
        axios.get('/api/user')
          .then(response => {
            commit('auth', response.data)
            resolve(response)
          }).catch(error => {
            commit('auth', false)
            reject(error)
          })
      })
    }
  },
  openDiscussion ({ commit }, payload) {
    commit('chatOn')
    commit('chatActive', payload)
  },
  serverList ({ commit }) {
    axios.get('/api/server-list')
      .then(response => {
        commit('updateServerList', response.data.data)
      })
  },
  ping ({ commit }) {
    return new Promise((resolve) => {
      queue.get('/api/ping', { data: { message: 'Running ping on API' } })
        .then(response => {
          resolve(response)
        })
    })
  },
  index ({ commit, state }, payload) {
    const collection = helpers.camelToKebab(payload.collection)
    const resourceState = helpers.firstToLower(helpers.kebabToCamel(collection)) + 'State'
    const resourceStore = helpers.firstToLower(helpers.kebabToCamel(collection)) + 'Store'

    // If we can auto-discover the resource state then use this to build the params
    const params = {}

    if (state[resourceState]) {
      Object.assign(params, {
        page: state[resourceState].page,
        count: state[resourceState].tableRows,
        sort: state[resourceState].sort.col,
        direction: state[resourceState].sort.dir,
        year: state[resourceState].year,
        ...state[resourceState].filters
      })
    }

    // If we have passed in custom params then we load them in here so they override any state
    if (payload.params) {
      Object.assign(params, {
        ...payload.params
      })
    }

    // Prepare the axios url and config
    let url = '/api/' + (payload.path ? helpers.camelToKebab(payload.path) : collection)
    if (payload.id) url += '/' + payload.id
    if (payload.tag) url += '/' + payload.tag
    if (payload.tag_id) url += '/' + payload.tag_id
    const config = {
      params: params ? Object.assign({}, params) : payload.params,
      data: payload.data ?? null
    }

    // Prepare the queue message
    if (params) {
      let message = ''
      if (params.date) {
        message = i18n.t('messages.loading_with_month', {
          month: DateTime.fromISO(params.date).toFormat('MMMM yyyy'),
          resource: helpers.camelToString(payload.collection)
        })
      } else if (params.year) {
        message = i18n.t('messages.loading_with_year', {
          year: params.year,
          resource: helpers.camelToString(payload.collection)
        })
      } else if (params.count) {
        message = i18n.t('messages.loading', {
          count: params.count,
          resource: helpers.camelToString(payload.collection),
          page: params.page ? params.page : 1
        })
      } else {
        message = i18n.t('messages.simple_loading', {
          resource: helpers.camelToString(payload.collection)
        })
      }
      if (!config.data) config.data = { message: message }
      else config.data.message = message
      if (state[resourceState] && state[resourceState].icon) config.data.icon = state[resourceState].icon
    }

    // If we have a local collection for this resource
    if (state[resourceStore]) {
      if (config.params) {
        config.params.page = null
        config.params.count = null
      }
      if (state[resourceStore].length === 0) {
        // Query the API first then return the resources
        return query(resourceStore).then(() => {
          filter()
        })
      } else {
        // Return the resources and query afterwards
        filter()
        return query(resourceStore).then(() => {
          filter()
        })
      }
    } else {
      // Otherwise process it as usual
      // Clear the current data from the resource collection
      commit('processResponse', { collection: helpers.kebabToCamel(collection), data: null })

      return query()
    }

    function query (store = null) {
      if (state.online) {
        return new Promise((resolve) => {
          queue.get(url, config)
            .then(response => {
              commit('processResponse', {
                collection: helpers.kebabToCamel(store ?? collection),
                resource: store,
                data: store ? response.data.data : response.data
              })
              resolve(response)
            })
            .catch(error => {
              // If we get an unauthenticated code, then log the user out
              if (error.response.status === 401) {
                commit('auth', null)
                router.push({ name: 'Login' })
              }
            })
        })
      }
    }

    function filter () {
      if (state[helpers.kebabToCamel(collection)]) {
        let data = state[resourceStore]
        if (state[resourceState].search && state[resourceState].search.active) {
          data = _.filter(data, function (o) {
            return JSON.stringify(o).toLowerCase().includes(state[resourceState].search.phrase.toLowerCase())
          })
        }
        data = _.sortBy(data, [function (o) {
          return o[params.sort]
        }])
        if (params.direction === 'desc') _.reverse(data)
        const pages = _.chunk(data, params.count)
        commit('processResponse', {
          collection: helpers.kebabToCamel(collection),
          data: {
            data: pages[params.page - 1],
            links: {
              first: '',
              last: '',
              next: '',
              prev: ''
            },
            meta: {
              current_page: params.page,
              from: 1,
              to: 20,
              last_page: pages.length,
              per_page: params.count,
              total: state[resourceStore].length
            }
          }
        })
      }
    }
  },
  show ({ commit }, payload) {
    const object = helpers.camelToKebab(helpers.pluralize(payload.object))
    const config = {
      url: '/api/' + object + '/' + payload.id,
      method: 'GET'
    }
    if (payload.blob) config.responseType = 'blob'
    return new Promise((resolve) => {
      axios(config).then(response => {
        commit('processResponse', { resource: helpers.singular(helpers.kebabToCamel(object)), data: response.data })
        resolve(response)
      })
    })
  },
  store ({ commit }, payload) {
    commit('submitting', true)
    const collection = helpers.camelToKebab(payload.collection)
    return new Promise((resolve) => {
      let url = '/api/' + (payload.path ? helpers.camelToKebab(payload.path) : collection)
      if (payload.id) url += '/' + payload.id
      if (payload.tag) url += '/' + payload.tag
      if (payload.tag_id) url += '/' + payload.tag_id
      submit.post(url, payload.resource)
        .then(response => {
          if (response.data) {
            commit('processResponse', { collection: helpers.kebabToCamel(collection), data: response.data })
          }
          commit('submitting', false)
          resolve(response)
        })
    })
  },
  chunk ({ state, commit }, payload) {
    commit('submitting', true)
    const CHUNK_SIZE = 524288
    const FILES = []
    let CHUNK_TOTAL = 1
    let CHUNK_PROCESSED = 0
    const formData = {}

    const buildFileArray = (file) => {
      const chunk = Math.ceil(file.size / CHUNK_SIZE)
      CHUNK_TOTAL += chunk
      const chunks = []

      for (let i = 0; i < chunk; i++) {
        chunks.push(file.slice(
          i * CHUNK_SIZE, Math.min(i * CHUNK_SIZE + CHUNK_SIZE, file.size), file.type
        ))
      }
      FILES.push({
        file: file,
        chunks: chunks
      })
    }

    for (const key in payload.resource) {
      if (payload.resource[key] instanceof File) {
        const file = payload.resource[key]
        buildFileArray(file)
      } else if (typeof payload.resource[key] === 'object') {
        for (const key2 in payload.resource[key]) {
          if (payload.resource[key][key2] instanceof File) {
            const file = payload.resource[key][key2]
            buildFileArray(file)
          } else {
            formData[key] = payload.resource[key] ?? ''
            break
          }
        }
      } else {
        formData[key] = payload.resource[key] ?? ''
      }
    }

    return new Promise((resolve) => {
      submit(payload.url, { method: 'POST', data: formData })
        .then(response => {
          commit('submitting', false)
          if (payload.collection && response.data) {
            commit('processResponse', { collection: helpers.firstToLower(helpers.kebabToCamel(payload.collection)), data: response.data })
          }
          resolve(response)

          CHUNK_PROCESSED++
          commit('updateChunks', { total: CHUNK_TOTAL, processed: CHUNK_PROCESSED, icon: payload.icon, message: payload.message })

          const upload = (file) => {
            if (!state.online) {
              setTimeout(() => {
                return upload(file)
              })
            } else if (file) {
              const fileData = new FormData()
              if (response.data && response.data.data.id) fileData.set('id', response.data.data.id)
              fileData.set('is_last', file.chunks.length === 1)
              fileData.set('file', file.chunks[0], `${file.file.name}.part`)

              submit(payload.url, {
                method: 'POST',
                data: fileData,
                headers: { 'Content-Type': 'application/octet-stream' }
              })
                .then((res) => {
                  CHUNK_PROCESSED++
                  commit('updateChunks', { processed: CHUNK_PROCESSED })
                  if (CHUNK_PROCESSED === CHUNK_TOTAL && payload.callback) {
                    payload.callback(res)
                  }
                  file.chunks.shift()
                  if (file.chunks.length > 0) return upload(file)
                  else if (file.chunks.length === 0 && FILES.length > 0) return upload(FILES.shift())
                })
                .catch((error) => {
                  // Too many requests
                  if (error.response && error.response.status === 429) {
                    const seconds = error.response.headers['Retry-After'] || 60
                    setTimeout(() => {
                      return upload(file)
                    }, seconds * 1000)
                  }
                  if (!state.online) return upload(file)
                })
            }
          }

          upload(FILES.shift())
        })
    })
  },
  update ({ commit }, payload) {
    commit('submitting', true)
    let collection, object
    if (payload.collection) collection = helpers.camelToKebab(payload.collection)
    if (payload.object) object = helpers.camelToKebab(payload.object)
    return new Promise((resolve) => {
      let url = '/api/' + (payload.path ? helpers.camelToKebab(payload.path) : (collection || object))
      if (payload.id) {
        url += '/' + payload.id
        if (payload.tag) {
          url += '/' + helpers.camelToKebab(payload.tag)
          if (payload.resource.id) url += '/' + payload.resource.id
        }
      } else if (payload.resource.id) url += '/' + payload.resource.id
      submit.put(url, payload.resource)
        .then(response => {
          if (payload.collection) commit('processResponse', { collection: helpers.kebabToCamel(collection), data: response.data })
          if (payload.object) commit('processResponse', { resource: helpers.kebabToCamel(object), data: response.data })
          commit('submitting', false)
          resolve(response)
        })
    })
  },
  destroy ({ commit }, payload) {
    commit('submitting', true)
    const collection = helpers.camelToKebab(payload.collection)
    return new Promise((resolve) => {
      let url = '/api/' + (payload.path ? helpers.camelToKebab(payload.path) : collection)
      if (payload.id) url += '/' + payload.id
      if (payload.tag) url += '/' + helpers.camelToKebab(payload.tag)
      if (payload.tag_id) url += '/' + payload.tag_id
      axios.delete(url)
        .then(response => {
          let data = { id: payload.id }
          if (payload.tag_id) data = { id: payload.tag_id }
          if (response.data) data = response.data
          commit('processResponse', { collection: helpers.kebabToCamel(collection), data: data })
          commit('submitting', false)
          resolve(response)
        })
    })
  },
  // indexComments ({ commit }, payload) {
  //   return new Promise((resolve) => {
  //     axios.get('/api/discussions/' + payload.id + '/comments', {
  //       params: payload.params
  //     })
  //       .then(response => {
  //         commit('appendComments', { id: payload.id, data: response.data })
  //         resolve(response)
  //       })
  //       .catch(error => {
  //         if (error.response.status === 401) {
  //           commit('auth', null)
  //           router.push({ name: 'Login' })
  //         }
  //       })
  //   })
  // },
  initialLoad ({ dispatch, commit }) {
    commit('initialLoad', 'reset')
    const promises = []

    promises.push(new Promise((resolve) => {
      dispatch('index', { collection: 'Users' })
        .then(response => { commit('initialLoad'); resolve(response) }, error => resolve(error))
    }))

    promises.push(new Promise((resolve) => {
      dispatch('index', { collection: 'Customers' })
        .then(response => { commit('initialLoad'); resolve(response) }, error => resolve(error))
    }))

    promises.push(new Promise((resolve) => {
      dispatch('index', { collection: 'Tasks' })
        .then(response => { commit('initialLoad'); resolve(response) }, error => resolve(error))
    }))

    promises.push(new Promise((resolve) => {
      dispatch('index', { collection: 'TaskGroups' })
        .then(response => { commit('initialLoad'); resolve(response) }, error => resolve(error))
    }))

    promises.push(new Promise((resolve) => {
      dispatch('index', { collection: 'allContacts' })
        .then(response => { commit('initialLoad'); resolve(response) }, error => resolve(error))
    }))

    promises.push(new Promise((resolve) => {
      dispatch('index', { collection: 'Projects' })
        .then(response => { commit('initialLoad'); resolve(response) }, error => resolve(error))
    }))

    return Promise.all(promises)
  }
}
